import { ThisReceiver } from "@angular/compiler";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  Inject,
  Injectable,
  Input,
  OnDestroy,
  OnInit,
} from "@angular/core";
import {
  AsyncValidatorFn,
  ControlContainer,
  ControlValueAccessor,
  Form,
  FormArray,
  FormBuilder,
  FormControl,
  FormControlStatus,
  FormGroup,
  ValidationErrors,
  Validator,
  ValidatorFn,
} from "@angular/forms";
import { Router } from "@angular/router";
import {
  BaseFpxChangeHandler,
  BaseFpxGridChangeHandler,
  BaseFpxLoadFormHandler,
} from "../models/index";
import { skip, Subject, Subscription, takeUntil } from "rxjs";

@Directive()
export abstract class BaseFpxGridComponent<
  Helper extends BaseFpxGridHelper<State>,
  State extends BaseFpxGridComponentState
> implements OnInit, OnDestroy, AfterViewInit, ControlValueAccessor, Validator
{
  @Input() formControlName!: string;
  @Input() changePublisher!: Subject<{
    subject: string;
    payload: any;
  }>;
  protected formGroup: FormGroup;
  protected subFormGroup!: FormGroup;
  formControl!: FormControl;

  protected columnHeaders: String[] = [];

  private readonly _syncValidators: ValidatorFn[] = [];
  private readonly _asyncValidators: AsyncValidatorFn[] = [];
  private readonly _formBuilder: FormBuilder;
  private readonly notifyContext: Map<string, any> = new Map<string, any>();
  private readonly _controlContainer: ControlContainer;
  private changePublisher$!: Subscription;
  private controlStatus$!: Subscription;
  private readonly _destroy$ = new Subject<any>();
  protected readonly _helper: Helper;
  private readonly _route: Router;
  private readonly _changeDetectorRef: ChangeDetectorRef;
  errorCode: string[] = [""];
  formChangePublisher$ = new Subject<{ subject: string; payload: any }>();
  columnWidth: number[] = [];
  gridWidth: number = 100;
  private gridFormControls: FormControlHolder[] = [];

  constructor(
    _formBuilder: FormBuilder,
    _controlContainer: ControlContainer,
    _route: Router,
    _helper: Helper,
    _changeDetectorRef: ChangeDetectorRef
  ) {
    this._formBuilder = _formBuilder;
    this._controlContainer = _controlContainer;
    this._route = _route;
    this._helper = _helper;
    this._changeDetectorRef = _changeDetectorRef;
    this.formGroup = this._formBuilder.group({}, { updateOn: "blur" });
  }

  writeValue(obj: Array<any>): void {
    console.log("writing to grid", obj);
    if (obj === null || (obj && obj.length != this.formArray.length)) {
      console.log("form array size", this.formArray.length);
      while (this.formArray.length !== 0) {
        this.formArray.removeAt(0);
      }
      this._changeDetectorRef.markForCheck();
    }

    if (obj && this.formArray) {
      if(this.formArray.controls.length == 0) {
        for (let index = 0; index < obj.length; index++) {
          console.log("creating row", index);
          this.addSubFormGroup();
        }
      }
      console.log("patching value ", obj);
      this.formArray.patchValue(obj, { emitEvent: false });
      this.formControl.setValue(this.formArray.getRawValue(), {
        emitModelToViewChange: false,
      });
      this._changeDetectorRef.markForCheck();
    }
  }

  setDisabledState(disable: boolean) {
    disable
      ? this.formArray.disable({ emitEvent: false })
      : this.formArray.enable({ emitEvent: false });
  }
  onChange: any = () => {};
  onTouched: () => void = () => {};
  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
  validate(): ValidationErrors | null {
    if (this.formArray?.invalid) {
      return { invalid: true };
    } else {
      return null;
    }
  }
  onValidationChange: any = () => {};
  registerOnValidatorChange?(fn: () => void): void {
    this.onValidationChange = fn;
  }

  protected addFormControl(
    name: string,
    defaultValue: any,
    syncValidatorFns: ValidatorFn[] = [],
    asyncValidatorFns: AsyncValidatorFn[] = [],
    updateOn: "change" | "blur" | "submit" = "blur",
    initialChangeDection: number = 0
  ) {
    let formControlHolder = new FormControlHolder();
    formControlHolder.name = name;
    formControlHolder.defaultValue = defaultValue;
    formControlHolder.syncValidatorFns = syncValidatorFns;
    formControlHolder.asyncValidatorFns = asyncValidatorFns;
    formControlHolder.updateOn = updateOn;
    formControlHolder.initialChangeDection = initialChangeDection;
    this.gridFormControls.push(formControlHolder);

    // let formControl = this._formBuilder.control(defaultValue, {
    //   validators: syncValidatorFns,
    //   asyncValidators: asyncValidatorFns,
    //   updateOn: updateOn,
    // });
    // this.gridFormControls.push(formControl);
    // this.gridFormControlNames.push(name);
    /*this.subFormGroup.addControl(name, formControl);
    const formIndex = this.formArray.length ?? 0;
    formControl.statusChanges
      .pipe(skip(initialChangeDection))
      .subscribe((status: FormControlStatus) => {
        this._helper.notifyChangeHandler(
          name,
          status,
          formControl.getRawValue(),
          this.formArray,
          formIndex
        );
      });
    formControl.valueChanges.subscribe((value: any) => {
      this.formChangePublisher$.next({ subject: name, payload: value });
    });*/
  }

  get formArray(): FormArray {
    return this.formGroup.get("formArray") as FormArray;
  }
  /** @description return the error code */
  private getErrorCode(index: number) {
    console.log(this.formArray.at(index).errors);
    return Object.keys(this.formArray.at(index).errors as ValidationErrors)[0];
  }

  public addRow() {
    let myFormControls: FormControl[] = [];
    this.subFormGroup = this._formBuilder.group({}, { updateOn: "blur" });

    for (let li = 0; li < this.gridFormControls.length; li++) {
      //console.log('Creating form control',this.gridFormControls[li].name);
      let formControl = this._formBuilder.control(
        this.gridFormControls[li].defaultValue,
        {
          validators: this.gridFormControls[li].syncValidatorFns,
          asyncValidators: this.gridFormControls[li].asyncValidatorFns,
          updateOn: this.gridFormControls[li].updateOn,
        }
      );

      this.subFormGroup.addControl(this.gridFormControls[li].name, formControl);
      formControl.updateValueAndValidity();
      const formIndex = this.formArray.length ?? 0;
      formControl.statusChanges
        .pipe(skip(this.gridFormControls[li].initialChangeDection))
        .subscribe((status: FormControlStatus) => {
          this._helper.notifyChangeHandler(
            this.gridFormControls[li].name,
            status,
            formControl.getRawValue(),
            this.formArray,
            formIndex
          );
        });
      formControl.valueChanges.subscribe((value: any) => {
        this.formChangePublisher$.next({
          subject: this.gridFormControls[li].name,
          payload: value,
        });
      });
    }

    this.subFormGroup.addValidators(this._syncValidators);
    console.log("pushing row");
    this.formArray.push(this.subFormGroup);
    this._changeDetectorRef.markForCheck();
  }

  addSubFormGroup() {
    this.addRow();
    this.formArray
      .at(this.formArray.length - 1)
      .statusChanges.subscribe((res: FormControlStatus) => {
        console.log("formrray", this.formArray.errors);
        this.onValidationChange();
        if (res === "INVALID") {
          // this.errorCode[this.formArray.length - 1] = this.getErrorCode(
          //   this.formArray.length - 1
          // );
        } else {
          this.errorCode[this.formArray.length - 1] = "";
        }
      });
    console.log(this.formArray.at(this.formArray.length - 1).errors);
  }

  deleteSubFormGroup(index: number) {
    this.formArray.removeAt(index);
  }

  ngOnInit(): void {
    this.gridWidth = this._helper.getGridWidth();
    this.columnWidth = this._helper.getGridColumnWidth();
    this._initializeForm();
    this.doPreInit();
    this.setGridDefinition();
    // this.formArray.addValidators(this._syncValidators);
    // this.formArray.addAsyncValidators(this._asyncValidators);
    // this.formArray.updateValueAndValidity();
    this.registerFormControl();
    this._helper.doPreInit();
  }

  registerFormControl() {
    let formGroup = this._controlContainer.control as FormGroup;
    if (this.formControlName) {
      this.formControl = formGroup.controls[
        this.formControlName
      ] as FormControl;
      this.formArray.valueChanges.pipe(takeUntil(this._destroy$)).subscribe({
        next: (res) => {
          this.formControl.setValue(this.formArray.getRawValue(), {
            emitModelToViewChange: false,
          });
        },
      });
      if (this.changePublisher) {
        this.changePublisher$ = this.changePublisher
          .asObservable()
          .subscribe((response) => {
            this.notifyContext.set(response.subject, response.payload);
            this.doChangeNotify(
              response.subject,
              response.payload,
              this.notifyContext
            );
          });
      }
    }
  }

  ngOnDestroy(): void {}

  ngAfterViewInit(): void {
    this.doPostInit();
    this._helper.doPostInit(this.formGroup);
    this._helper.doAfterPostInit(this.formGroup);
  }

  protected addSyncValidatorFn(syncValidatorFn: ValidatorFn) {
    this._syncValidators.push(syncValidatorFn);
  }

  protected addAsyncValidatorFn(asyncValidatorFn: AsyncValidatorFn) {
    this._asyncValidators.push(asyncValidatorFn);
  }

  protected doReset(formGroup: FormGroup): void {}
  protected doSubmit(formGroup: FormGroup): void {}
  protected doPreInit(): void {}
  protected doPostInit(): void {}
  protected doDestroy(): void {}
  protected setGridDefinition(): void {}

  private _initializeForm() {
    this.formGroup = this._formBuilder.group({
      formArray: this._formBuilder.array([]),
    });
  }
  protected doChangeNotify(
    subject: string,
    payload: any,
    context: Map<string, any>
  ): void {}

  // protected setGridColumnWidth(columnWidth: number[]) {
  //   this.columnWidth = columnWidth;
  // }

  // protected setGridWidth(gridWidth: number) {
  //   this.gridWidth = gridWidth;
  // }
}

export abstract class BaseFpxGridComponentState {}

@Injectable()
export abstract class BaseFpxGridHelper<
  State extends BaseFpxGridComponentState
> {
  public doPostInit(formGroup): void {}
  public doAfterPostInit(formGroup): void {}

  protected state: State;

  protected handlerMap: Map<String, BaseFpxGridChangeHandler> = new Map();

  protected loadFormMap: Map<String, BaseFpxLoadFormHandler<any>> = new Map();

  constructor(@Inject("_state") _state: State) {
    this.state = _state;
  }

  public getState(): State {
    return this.state;
  }

  protected addChangeHandler(
    name: String,
    changeHandler: BaseFpxGridChangeHandler
  ) {
    this.handlerMap.set(name, changeHandler);
  }

  protected addLoadFormHandler(
    name: string,
    loadFormHandler: BaseFpxLoadFormHandler<any>
  ) {
    this.loadFormMap.set(name, loadFormHandler);
  }

  public notifyLoadFormHandler(
    name: string,
    taskId: string,
    formGroup: FormGroup
  ) {
    if (this.loadFormMap.has(name)) {
      (this.loadFormMap as Map<string, any>).get(name)(taskId, formGroup);
    }
  }

  public notifyChangeHandler(
    name: string,
    status: FormControlStatus,
    value: any,
    formGroup: FormArray,
    formIndex: number
  ) {
    if (this.handlerMap.has(name)) {
      (this.handlerMap as Map<string, any>).get(name)(
        name,
        status,
        value,
        formGroup,
        formIndex
      );
    }
  }

  protected value = (
    formcontrolName: string[],
    formArray: FormArray,
    formIndex: number
  ): string[] => {
    return formcontrolName.map((control) =>
      formArray.at(formIndex).get(control)?.getRawValue()
    );
  };

  protected setDisable(
    formcontrolName: string[],
    formGroup: FormGroup,
    isDisable: boolean
  ): void {
    formcontrolName.forEach((controls) => {
      isDisable
        ? formGroup.get(controls)?.disable({ emitEvent: false })
        : formGroup.get(controls)?.disable({ emitEvent: false });
    });
  }

  public abstract getGridWidth(): number;
  public abstract getGridColumnWidth(): number[];
  public doPreInit(): void {}
}

class FormControlHolder {
  name: string = "";
  defaultValue: any;
  syncValidatorFns: ValidatorFn[] = [];
  asyncValidatorFns: AsyncValidatorFn[] = [];
  updateOn: "change" | "blur" | "submit" = "blur";
  initialChangeDection: number = 0;
}
